<template>
  <div class="typing-game-intro-message">
    <p>
      Try this typing game out, let's see how well you can type, one mistake in a word and you'll
      have to retype that word. A random Wikipedia page summary is generated each time.
    </p>
  </div>
  <!-- The link to the Wikipedia page -->
  <transition name="fade" mode="out-in">
    <div v-if="wikiPageURL && wikiLoaded" class="text-center my-4">
      <a :href="wikiPageURL" target="_blank"
        >Read the full Wikipedia article on <strong>{{ wikiPageTitle }}</strong></a
      >
    </div>
  </transition>

  <transition name="fade" mode="out-in">
    <div v-if="wikiLoaded" class="typing-game-settings-bar pl-2 pr-6">
      <div class="typing-game-wpm" @click="hideMenu">
        <div class="flex gap-1 items-center">
          <div class="flex-auto relative flex items-center justify-center">
            <Transition name="fire-fade">
              <div v-if="formattedWordsPerMinute > BG_MUSIC_WPM" class="fire-container">
                <div class="fire">
                  <div v-for="n in 100" :key="n" class="particle"></div>
                </div>
              </div>
            </Transition>

            <div
              class="wpm-text z-10 whitespace-nowrap"
              :class="{ 'speed-warp text-orange-500': formattedWordsPerMinute > BG_MUSIC_WPM }"
            >
              Words Per Minute:
              <strong>{{ formattedWordsPerMinute }}</strong>
            </div>
          </div>
          <div class="flex-auto">
            Mistyped Words:
            <strong>{{ mistypedWordCounter }}</strong>
          </div>
          <div class="flex-auto">
            Time Elapsed:
            <strong
              >{{ Math.floor((elapsedTime / 1000 / 60) % 60) }}:{{
                ((elapsedTime / 1000) % 60).toFixed(0).padStart(2, "0")
              }}</strong
            >
          </div>
        </div>
      </div>
      <a class="typing-game-settings-button" href="#" @click.prevent="showMenu = !showMenu">
        <CogIcon class="typing-game-settings-icon" />
      </a>
      <transition name="fade" mode="out-in">
        <div v-show="showMenu" class="typing-game-settings-menu bg-gray-300 pl-4 z-20">
          <TypingGameSettings
            @capitals="checkCapOption"
            @punct="checkPunctOption"
            @mute="applyOptionMute"
          />
        </div>
      </transition>
    </div>
  </transition>
  <transition name="fade" mode="out-in">
    <div v-if="wikiLoaded" id="windowID" class="typing-game-window" @click="onClickGameWindow">
      <!-- NEW: Smooth Floating Cursor -->
      <div class="smooth-cursor" :style="cursorStyle"></div>

      <input
        ref="mobileKeyboard"
        type="text"
        inputmode="text"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        class="mobile-keyboard-input"
      />
      <div
        v-for="(wordValue, wordIndex) in currentTokenizedLetters"
        :key="wordIndex"
        class="word-container"
      >
        <span
          v-for="(letterValue, letterIndex) in wordValue"
          :id="'letter-' + wordIndex + '-' + letterIndex"
          :key="wordIndex + ',' + letterIndex"
          :class="[
            'base-letter-format',
            letterIDsTypedDict[wordIndex + ',' + letterIndex]
              ? letterIDsTypedDict[wordIndex + ',' + letterIndex]
              : '',
            wordIndex + ',' + letterIndex == currentLetterID ? activeLetterClass : ''
          ]"
        >
          {{ letterValue }}
        </span>
        <span
          v-if="wordIndex < currentTokenizedLetters.length - 1"
          :id="'letter-s' + wordIndex"
          :key="'s' + wordIndex"
          :class="[
            'base-letter-format',
            letterIDsTypedDict['s' + wordIndex] ? letterIDsTypedDict['s' + wordIndex] : '',
            's' + wordIndex == currentLetterID ? activeLetterClass : ''
          ]"
        >
          &nbsp;
        </span>
      </div>
    </div>
  </transition>

  <div v-if="wikiLoaded && completedExtract" class="typing-game-completed">
    <div class="typing-game-intro-message">
      You did it! You can retry the same passage by clicking on Restart or a new passsage by
      clicking on Another.
    </div>
  </div>

  <transition name="fade">
    <div v-if="wikiLoaded" class="typing-game-selections">
      <v-row class="justify-center typing-game-button-groups">
        <v-col cols="auto">
          <v-btn class="typing-game-button" elevation="2" @click="restart">
            Restart (Shift + Space)
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn class="typing-game-button" elevation="2" @click="another">
            Another (Shift + Enter)
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </transition>

  <audio ref="myAudio" autoplay loop muted>
    <source src="@/assets/audio/initiald-runninginthe90scut.mp3" type="audio/mp3" />
  </audio>
</template>

<script setup lang="ts">
import {
  ref,
  onMounted,
  onUnmounted,
  computed,
  reactive,
  provide,
  toRefs,
  useTemplateRef,
  nextTick,
  watch
} from "vue"
import unidecode from "unidecode"
import onKeyDown from "@/assets/scripts/onKeyDown"
import CogIcon from "@/components/cogicon.vue"
import TypingGameSettings from "@/components/TypingGameSettings.vue"
import router from "@/router"
import { useRoute } from "vue-router"

const props = defineProps<{
  title: string
}>()

const route = useRoute()
const { title } = toRefs(props)

const localTitle = ref<string>((route.params.title as string) || title.value)

watch(
  () => route.params,
  (newParams) => {
    if (newParams.title) {
      localTitle.value = newParams.title as string
    }
  }
)

onMounted(() => {
  isMute.value = localStorage.getItem("optionMute") === "true"
  myAudio.value.volume = "0"
  getWikiExtract()
  window.addEventListener("resize", updateCursorPosition)
})

onUnmounted(() => {
  window.removeEventListener("resize", updateCursorPosition)
})

const wikiLoaded = ref<boolean>(false)
const wikiPageURL = ref<string>("")
const wikiPageTitle = ref<string>("")
const currentTokenizedLetters = ref<string[][]>([])
const currentLetterID = ref<string>("0,0")
const letterIDsTypedDict = ref<any>({})

const currentMistypedWord = ref<boolean[]>([])
const isWordCloned = ref<boolean[]>([])

const activeLetterClass = ref<string>("active-letter")
const completedExtract = ref<boolean>(false)

const timer = ref<number>(0)
const elapsedTime = ref<number>(0)
const mistypedElapsedTime = ref<number>(0)
const mistypedLetterCounter = ref<number>(0)
const wordsCompleted = ref<number>(0)
const mistypedWordCounter = ref<number>(0)

const isMute = ref<boolean>(false)
const myAudio = ref<any>(null)
const backgroundAudioState = ref<string>("paused")

const showMenu = ref<boolean>(false)
const mobileKeyboard = useTemplateRef("mobileKeyboard")

const BG_MUSIC_WPM = ref<number>(90)

// -------- CURSOR LOGIC -------- //
const cursorStyle = ref({
  translate: "0px 0px 0.01px",
  width: "0px",
  opacity: "0"
})

const updateCursorPosition = async () => {
  await nextTick()
  const safeId = currentLetterID.value.replace(",", "-")
  const el = document.getElementById("letter-" + safeId)
  const container = document.getElementById("windowID")

  if (el && container) {
    const elRect = el.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    // Calculate relative to the scrolling container boundary
    const top = elRect.bottom - containerRect.top + container.scrollTop + 2
    const left = elRect.left - containerRect.left + container.scrollLeft

    cursorStyle.value = {
      translate: `${left}px ${top}px 0.01px`,
      width: `${elRect.width}px`,
      opacity: "1"
    }
  }
}

// Watch both position and the word map to re-calculate smoothly
watch([currentLetterID, currentTokenizedLetters], updateCursorPosition, { deep: true })
// ------------------------------ //

const applyTokenization = (passage: string) => {
  const returnArray = []
  const tokenizedWords = JSON.parse(JSON.stringify(passage.split(" ")))
  for (let i = 0; i < tokenizedWords.length; ++i) {
    const tempWord = tokenizedWords[i].split("")
    returnArray.push(tempWord)
  }
  return returnArray
}

const initTokenizedLetters = () => {
  currentTokenizedLetters.value.splice(
    0,
    currentTokenizedLetters.value.length,
    ...applyTokenization(store.getDisplayLetters()!)
  )
  isWordCloned.value = new Array(currentTokenizedLetters.value.length).fill(false)
  currentMistypedWord.value = new Array(currentTokenizedLetters.value.length).fill(false)
}

const store = {
  debug: false,
  state: reactive({
    displayLetters: new Map<number, string>(),
    stateNumber: 0,
    isRemoveCap: localStorage.getItem("optionCapitals") === "true",
    isRemovePunct: localStorage.getItem("optionPunctuation") === "true",
    isReset: false
  }),
  setDisplayLetters(original: string) {
    if (this.state.isReset) {
      const newValue = this.state.displayLetters.get(0)!
      this.state.displayLetters.set(1, newValue)
      this.state.displayLetters.set(2, newValue.toLowerCase())
      this.state.displayLetters.set(
        3,
        newValue
          .replace(/[^A-Za-z0-9\s]/g, "")
          .replace("  ", " ")
          .replace("  ", " ")
      )
      this.state.displayLetters.set(
        4,
        newValue
          .toLowerCase()
          .replace(/[^A-Za-z0-9\s]/g, "")
          .replace("  ", " ")
          .replace("  ", " ")
      )
      this.state.isReset = false
    } else {
      this.state.displayLetters.set(0, original)
      this.state.displayLetters.set(1, original)
      this.state.displayLetters.set(2, original.toLowerCase())
      this.state.displayLetters.set(
        3,
        original
          .replace(/[^A-Za-z0-9\s]/g, "")
          .replace("  ", " ")
          .replace("  ", " ")
      )
      this.state.displayLetters.set(
        4,
        original
          .toLowerCase()
          .replace(/[^A-Za-z0-9\s]/g, "")
          .replace("  ", " ")
          .replace("  ", " ")
      )
    }
  },
  getDisplayLetters() {
    return this.state.displayLetters.get(this.state.stateNumber)
  },
  setStateNumber() {
    if (this.state.isRemoveCap && this.state.isRemovePunct) this.state.stateNumber = 4
    else if (this.state.isRemovePunct) this.state.stateNumber = 3
    else if (this.state.isRemoveCap) this.state.stateNumber = 2
    else this.state.stateNumber = 1
  },
  setIsRemoveCap(newValue: boolean) {
    this.state.isRemoveCap = newValue
  },
  setIsRemovePunct(newValue: boolean) {
    this.state.isRemovePunct = newValue
  },
  setIsReset(newValue: boolean) {
    this.state.isReset = newValue
  }
}

provide("store", store)

const formattedWordsPerMinute = computed(() => {
  return Number.isFinite(Math.round(wordsCompleted.value / (elapsedTime.value / 1000 / 60)) || 0)
    ? Math.round(wordsCompleted.value / (elapsedTime.value / 1000 / 60)) || 0
    : 0
})

const fetchWikiData = async () => {
  const apiUrl = localTitle.value
    ? `https://en.wikipedia.org/api/rest_v1/page/summary/${title.value}`
    : "https://en.wikipedia.org/api/rest_v1/page/random/summary"

  const response = await fetch(apiUrl)
  const body = await response.json()
  return {
    url: body.content_urls.desktop.page,
    title: body.title,
    extract: unidecode(body.extract)
      .replace("\u000A", " ")
      .replace("  ", " ")
      .replace("  ", " ")
      .trim()
  }
}

const getWikiExtract = async () => {
  const data = await fetchWikiData()

  wikiPageURL.value = data.url
  wikiPageTitle.value = data.title

  store.setDisplayLetters(data.extract)
  store.setStateNumber()

  initTokenizedLetters()

  if (!localTitle.value) {
    const urlParts = data.url.split("/")
    const routeAppendix = urlParts[urlParts.length - 1]
    router.push(`/projects/perfect-typing/${routeAppendix}`)
  }
  wikiLoaded.value = true
}

const playBGMusic = () => {
  let actualVolumeFadeOut = 0.03 //Volume set to the audio player from 0 to 1
  let actualVolumeFadeIn = 0

  myAudio.value.muted = isMute.value
  if (formattedWordsPerMinute.value > BG_MUSIC_WPM.value) {
    if (backgroundAudioState.value === "paused") {
      backgroundAudioState.value = "playing"

      const fadeInInterval = setInterval(() => {
        actualVolumeFadeIn = parseFloat((actualVolumeFadeIn + 0.01).toFixed(2))
        if (actualVolumeFadeIn <= actualVolumeFadeOut) {
          myAudio.value.volume = actualVolumeFadeIn.toString()
        } else if (actualVolumeFadeIn > actualVolumeFadeOut) {
          clearInterval(fadeInInterval)
        }
      }, 100)
      return false
    }
  } else {
    if (backgroundAudioState.value === "playing") {
      backgroundAudioState.value = "paused"
      const fadeOutInterval = setInterval(() => {
        actualVolumeFadeOut = parseFloat((actualVolumeFadeOut - 0.01).toFixed(2))
        if (actualVolumeFadeOut >= actualVolumeFadeIn) {
          myAudio.value.volume = actualVolumeFadeOut.toString()
        } else if (actualVolumeFadeOut < actualVolumeFadeIn) {
          clearInterval(fadeOutInterval)
        }
      }, 100)
      return false
    }
  }
}

const startTimer = () => {
  if (timer.value === undefined) {
    return
  }
  timer.value = setInterval(() => {
    elapsedTime.value += 1000
    mistypedElapsedTime.value += 1000
  }, 1000)
}
const stopTimer = () => {
  clearInterval(timer.value)
}
const resetTimer = () => {
  elapsedTime.value = 0
}

const resetMistypedTimerandCounter = () => {
  mistypedElapsedTime.value = 0
  mistypedLetterCounter.value = 0
}

const onClickGameWindow = () => {
  hideMenu()
  bringUpMobileKeyboard()
}
const hideMenu = () => {
  showMenu.value = false
}

const bringUpMobileKeyboard = () => {
  mobileKeyboard.value?.focus()
}

const restart = () => {
  currentLetterID.value = "0,0"
  letterIDsTypedDict.value = {}
  wordsCompleted.value = 0
  completedExtract.value = false
  mistypedWordCounter.value = 0
  store.setIsReset(true)
  store.setDisplayLetters("")

  initTokenizedLetters()

  resetMistypedTimerandCounter()
  stopTimer()
  resetTimer()
}

const another = async () => {
  wikiLoaded.value = false
  restart()
  localTitle.value = "" // Reset the title prop
  await getWikiExtract() // Fetch new extract
}

const checkCapOption = (param: boolean) => {
  store.setIsRemoveCap(param)
  store.setStateNumber()
  restart()
  initTokenizedLetters()
}

const checkPunctOption = (param: boolean) => {
  store.setIsRemovePunct(param)
  store.setStateNumber()
  restart()
  initTokenizedLetters()
}

const applyOptionMute = (param: boolean) => {
  isMute.value = param
  myAudio.value.muted = isMute.value
}

const handleKey = (event: KeyboardEvent) => {
  /*Shift + Spacebar to Restart*/
  if (event.shiftKey && event.key === " ") {
    restart()
    return
  }
  /*Shift + Enter for another passage*/
  if (event.shiftKey && event.key === "Enter") {
    another()
    return
  }

  if (currentLetterID.value === "0,0" && mistypedLetterCounter.value === 0) {
    stopTimer()
    resetTimer()
    startTimer()
  }

  playBGMusic()
  hideMenu()

  const keyPressed = event.key

  const coordinateString = currentLetterID.value.split(",")
  const wordCoordinate: any = coordinateString[0]
  const letterCoordinate: any = coordinateString[1]
  const spaceCoordinate: string = currentLetterID.value.includes("s")
    ? currentLetterID.value.substring(1, currentLetterID.value.length)
    : ""
  //   // DEBUGGING
  //   console.log("keypress: " + keyPressed);
  //   console.log("currentLetterID: " + currentLetterID.value);
  //  console.log("coordinateString: " + coordinateString);
  //   console.log("wordCoordinate: " + wordCoordinate);
  //  console.log("letterCoordinate: " + letterCoordinate);
  //   console.log("spaceCoordinate: " + spaceCoordinate);

  // If Shift or enter is pressed
  if (keyPressed === "Enter" || keyPressed === "Shift") {
    event.preventDefault()
    return
  }

  // If keypressed is backspace or delete
  if ((keyPressed === "Backspace" || keyPressed === "Delete") && !completedExtract.value) {
    letterIDsTypedDict.value[currentLetterID.value] = ""
    // Case where current value is a space, and user presses backspace
    if (currentLetterID.value.substring(0, 1) === "s") {
      currentLetterID.value =
        clamp(parseInt(spaceCoordinate), 0, 999) +
        "," +
        (currentTokenizedLetters.value[wordCoordinate.replace("s", "")].length - 1).toString()
      // Case where the current value is the beginning of a word
    } else if (parseInt(letterCoordinate) === 0) {
      if (parseInt(wordCoordinate) > 0) {
        currentLetterID.value = "s" + (parseInt(wordCoordinate) - 1)
      } else {
        currentLetterID.value = "0,0"
      }
    } else {
      currentLetterID.value = wordCoordinate + "," + clamp(parseInt(letterCoordinate) - 1, 0, 999)
    }
    letterIDsTypedDict.value[currentLetterID.value] = ""

    event.preventDefault()
    return
  }

  // If current letter is a space
  if (currentLetterID.value.substring(0, 1) === "s") {
    const spaceWordIndex = parseInt(spaceCoordinate, 10)
    const isCloned = isWordCloned.value[spaceWordIndex]

    if (keyPressed === " ") {
      resetMistypedTimerandCounter()
      letterIDsTypedDict.value[currentLetterID.value] = isCloned
        ? "letter-correct-cloned"
        : "letter-correct"
    } else {
      letterIDsTypedDict.value[currentLetterID.value] = isCloned
        ? "letter-incorrect-cloned"
        : "letter-incorrect"

      if (!currentMistypedWord.value[spaceWordIndex]) {
        const targetIndex = spaceWordIndex + 1
        currentTokenizedLetters.value.splice(targetIndex, 0, [
          ...currentTokenizedLetters.value[spaceWordIndex]
        ])
        isWordCloned.value.splice(targetIndex, 0, true) // Mark new word as clone
        currentMistypedWord.value.splice(targetIndex, 0, false) // Keep sync

        currentMistypedWord.value[spaceWordIndex] = true
        mistypedWordCounter.value++
      }

      mistypedLetterCounter.value++
    }

    currentLetterID.value = `${spaceWordIndex + 1},0`
    event.preventDefault()
    return
  }
  //If current letter is a character
  else {
    const isCloned = isWordCloned.value[wordCoordinate]

    if (keyPressed === currentTokenizedLetters.value[wordCoordinate][letterCoordinate]) {
      letterIDsTypedDict.value[currentLetterID.value] = isCloned
        ? "letter-correct-cloned"
        : "letter-correct"
      resetMistypedTimerandCounter()
    } else {
      if (!currentMistypedWord.value[wordCoordinate]) {
        const targetIndex = parseInt(wordCoordinate) + 1
        currentTokenizedLetters.value.splice(targetIndex, 0, [
          ...currentTokenizedLetters.value[wordCoordinate]
        ])
        isWordCloned.value.splice(targetIndex, 0, true) // Mark new word as clone
        currentMistypedWord.value.splice(targetIndex, 0, false) // Keep sync

        currentMistypedWord.value[wordCoordinate] = true
        mistypedWordCounter.value++
      }

      mistypedLetterCounter.value++
      letterIDsTypedDict.value[currentLetterID.value] = isCloned
        ? "letter-incorrect-cloned"
        : "letter-incorrect"
    }
  }

  if (!completedExtract.value) {
    if (parseInt(letterCoordinate) >= currentTokenizedLetters.value[wordCoordinate].length - 1) {
      currentLetterID.value = "s" + parseInt(wordCoordinate)
      wordsCompleted.value++
    } else {
      currentLetterID.value = wordCoordinate + "," + (parseInt(letterCoordinate) + 1)
    }
  }
  // Completed typing the passage!
  if (
    parseInt(currentLetterID.value.substring(1, currentTokenizedLetters.value.length - 1)) ==
    currentTokenizedLetters.value.length - 1
  ) {
    stopTimer()
    completedExtract.value = true
    cursorStyle.value.opacity = "0" // Hide cursor entirely on completion
    return
  }
}

onKeyDown(handleKey)

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max)
</script>

<style lang="scss" scoped>
@use "sass:math";

.typing-game-intro-message {
  margin-top: 15px;
  text-align: center;
  margin-left: 25px;
  margin-right: 25px;
  line-height: 2;
  position: relative;
  margin-bottom: 15px;
}
.typing-game-window {
  margin-left: 25px;
  margin-right: 25px;
  padding-left: 25px;
  padding-right: 25px;
  padding-top: 15px;
  padding-bottom: 15px;
  background-color: #ffffff;
  overflow: hidden;
  text-align: left !important;
  position: relative; /* REQUIRED for the cursor absolute positioning */
}

/* NEW CURSOR STYLING */
.smooth-cursor {
  position: absolute;
  top: 0;
  left: 0;
  height: 4px;
  background-color: rgb(96, 52, 255);
  border-radius: 4px;
  pointer-events: none;
  z-index: 10;
  /* Super smooth tracking transition */
  transition:
    translate 0.1s ease-out,
    width 0.1s ease-out;
  /* Optional but highly recommended: tells Firefox/Chrome to expect changes here */
  will-change: translate, width;
  /* Pulsing size */
  animation: cursorPulse 0.8s infinite alternate ease-in-out;
}

@keyframes cursorPulse {
  0% {
    scale: 1;
  }
  100% {
    scale: 1.2;
  }
}
/* ----------------- */

.base-letter-format {
  display: inline-block;
  color: #19242f;
  line-height: 1;
  font-family: Consolas, monaco, monospace;
  font-size: 26px;
  margin-top: 4px;
  margin-bottom: 4px;
}
.word-container {
  white-space: nowrap;
  display: inline-block;
}

.active-letter {
  color: rgb(96, 52, 255);
  font-weight: bold;
}

.letter-correct {
  color: rgb(22, 87, 50);
  background-color: rgb(181, 255, 172);
}
.letter-incorrect {
  color: rgb(96, 52, 255);
  background-color: rgb(255, 163, 182);
}

.letter-correct-cloned {
  color: rgb(22, 87, 50);
  background-color: rgb(215, 255, 130);
}
.letter-incorrect-cloned {
  color: rgb(96, 52, 255);
  background-color: rgb(255, 200, 150);
}

@keyframes fadeIn-game-complete {
  from {
    opacity: 0;
  }
}
.typing-game-selections {
  position: relative;
  margin-top: 15px;
  margin-bottom: 15px;
  text-align: center;
}
.typing-game-completed {
  animation: fadeIn-game-complete 0.5s linear;
  opacity: 1;
  margin-top: 15px;
  margin-bottom: 15px;
  text-align: center;
}
.typing-game-button-groups {
  gap: 16px;
}
.typing-game-button {
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #dcdcdc;
  display: inline-block;
  cursor: pointer;
  color: #666666;
  font-family: Arial;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  text-shadow: 0px 1px 0px #ffffff;
}
.typing-game-button:hover {
  background: linear-gradient(to bottom, #e9e9e9 5%, #f9f9f9 100%);
  background-color: #e9e9e9;
}
.typing-game-button:active {
  position: relative;
  top: 1px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.typing-game-settings-bar {
  position: relative;
  background-color: #e7eaed;
  margin-left: 25px;
  margin-right: 25px;
  text-align: center;
}
.typing-game-wpm {
  position: relative;
  color: #19242f;
}
.typing-game-settings-button {
  color: #19242f;
  position: absolute;
  top: 2px;
  right: 2px;
}
.typing-game-settings-icon {
  width: 20px;
}
.typing-game-settings-menu {
  z-index: 30;
  position: absolute;
  top: 24px;
  right: 0px;
}
.mobile-keyboard-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

/******Fire effect when greater than 90 WPM*******/
.fire-container {
  position: absolute;
  right: 65%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 0.5rem;
  pointer-events: none;
  z-index: 0;
}

$fireColor: rgb(255, 80, 0);
$fireColorT: rgba(255, 80, 0, 0);
$dur: 1s;
$blur: 0.02em;
$fireRad: 3em;
$parts: 100;
$partSize: 2em;

.fire {
  font-size: 24px;
  filter: blur($blur);
  -webkit-filter: blur($blur);
  position: relative;
  width: 6em;
  height: 1.5em;
}
.particle {
  animation: rise $dur ease-in infinite;
  background-image: radial-gradient($fireColor 20%, $fireColorT 70%);
  border-radius: 50%;
  mix-blend-mode: screen;
  opacity: 0;
  position: absolute;
  bottom: 0;
  width: $partSize;
  height: $partSize;
  // spread particles out in time and x-position to get desired effect
  @for $p from 1 through $parts {
    &:nth-of-type(#{$p}) {
      animation-delay: $dur * math.random();
      left: calc((100% - #{$partSize}) * #{math.div(($p - 1), $parts)});
    }
  }
}
@keyframes rise {
  from {
    opacity: 0;
    transform: translateX(0) translateY(0) scale(1);
  }
  25% {
    opacity: 0.5;
  }
  to {
    opacity: 0;
    transform: translateX(-15em) translateY(-5em) scale(0);
  }
}

/* Fades in and out over 1 second so the flames build up/die down naturally */
.fire-fade-enter-active,
.fire-fade-leave-active {
  transition: opacity 1s ease-in-out;
}

.fire-fade-enter-from,
.fire-fade-leave-to {
  opacity: 0;
}

/*************/

/* --- High Speed WPM Shake & Warp --- */
.wpm-text {
  transform: skewX(0deg);
  transform-origin: bottom;
  /* Smoothly transition the lean and the color over half a second */
  transition:
    transform 0.5s ease-out,
    color 0.5s ease-out;
}

.speed-warp {
  /* The skew is handled by the transition above */
  transform: skewX(-15deg);
  /* The rattle animation */
  animation: carRattle 0.1s infinite linear;
}

@keyframes carRattle {
  0% {
    translate: 0px 0px;
  }
  25% {
    translate: 1px 1px;
  }
  50% {
    translate: -1px -1px;
  }
  75% {
    translate: -1px 1px;
  }
  100% {
    translate: 1px -1px;
  }
}
</style>
